import React, { useState } from 'react';
import { registerAmbassador, sendAmbassadorOTP } from '../../services/api';
import Alert from '../common/Alert';
import '../common/RegistrationForm.css';

const AmbassadorRegistration = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    institutionName: '',
    commissionPercentage: '',
    adminEmail: '',
    aadhar: '',
    panCard: ''
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');

  const [alert, setAlert] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const adminEmails = [
    'alok.iitdelhi@educatory.ac',
    'cfo@educatory.ac',
    'tech@educatory.ac'
  ];

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!formData.adminEmail) {
      setAlert({ type: 'error', message: 'Please select an admin email for OTP verification.' });
      return;
    }

    setIsSubmitting(true);
    setAlert(null);
    try {
      await sendAmbassadorOTP({ email: formData.adminEmail });
      setOtpSent(true);
      setAlert({ type: 'success', message: `OTP sent to ${formData.adminEmail}. Please check with the admin.` });
    } catch (error) {
      setAlert({ type: 'error', message: error.response?.data?.message || 'Failed to send OTP' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otp) {
      setAlert({ type: 'error', message: 'Please enter the OTP.' });
      return;
    }

    setIsSubmitting(true);
    setAlert(null);

    try {
      const response = await registerAmbassador({ ...formData, otp });
      setAlert({
        type: 'success',
        message: `Registration successful! Your referral code is ${response.referralCode}`
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        institutionName: '',
        commissionPercentage: '',
        adminEmail: '',
        aadhar: '',
        panCard: ''
      });
      setOtp('');
      setOtpSent(false);
    } catch (error) {
      setAlert({
        type: 'error',
        message: error.response?.data?.message || 'Registration failed'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-container">
      <div className="registration-form-wrapper">
        <img
          src="/assets/BigLogo.png"
          alt="Educatory Logo"
          className="educatory-logo"
        />
        <h2>Ambassador Registration</h2>
        <p className="subtitle">Join our exclusive ambassador program and earn dynamic commissions</p>

        {alert && (
          <Alert
            type={alert.type}
            message={alert.message}
            onClose={() => setAlert(null)}
          />
        )}

        <form onSubmit={otpSent ? handleSubmit : handleSendOTP} className="registration-form">
          <div className="form-row">
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                disabled={otpSent}
                placeholder="Enter your full name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={otpSent}
                placeholder="Enter your email"
              />
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                disabled={otpSent}
                placeholder="Enter 10-digit number"
                pattern="[0-9]{10}"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Institution/Organization Name</label>
              <input
                type="text"
                value={formData.institutionName}
                onChange={(e) => setFormData({ ...formData, institutionName: e.target.value })}
                required
                disabled={otpSent}
                placeholder="Enter institution name"
              />
            </div>

            <div className="form-group">
              <label>Commission Percentage (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.commissionPercentage}
                onChange={(e) => setFormData({ ...formData, commissionPercentage: e.target.value })}
                required
                disabled={otpSent}
                placeholder="Enter commission % (e.g., 20)"
              />
            </div>
          </div>

          <div className="form-info-message">
            <small style={{ color: '#64748b', fontStyle: 'italic', display: 'block', marginBottom: '8px' }}>
              * As cash transfers would be involved for the commission payouts, we request your Aadhar and PAN details.
            </small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Aadhar Number</label>
              <input
                type="text"
                value={formData.aadhar}
                onChange={(e) => setFormData({ ...formData, aadhar: e.target.value })}
                disabled={otpSent}
                placeholder="Enter 12-digit Aadhar"
                pattern="[0-9]{12}"
              />
            </div>

            <div className="form-group">
              <label>PAN Card</label>
              <input
                type="text"
                value={formData.panCard}
                onChange={(e) => setFormData({ ...formData, panCard: e.target.value.toUpperCase() })}
                disabled={otpSent}
                placeholder="Enter PAN Number"
                pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Select Email for OTP Verification</label>
              <select
                value={formData.adminEmail}
                onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                required
                disabled={otpSent}
              >
                <option value="">-- Select Admin Email --</option>
                {adminEmails.map(email => (
                  <option key={email} value={email}>{email}</option>
                ))}
              </select>
            </div>
          </div>

          {otpSent && (
            <div className="form-row">
              <div className="form-group otp-group">
                <label>Enter OTP</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  placeholder="Enter 6-digit OTP"
                  maxLength="6"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : (otpSent ? 'Verify OTP & Register' : 'Send OTP')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AmbassadorRegistration;
